// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { TryPremiumEntryPoint } from "@ms/uno-constants/lib/local/UpsellConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
// Controls
import { HeaderOverflowMenuContainer } from "@ms/planner-container-controls/lib/local/containers/headerOverflowMenu/HeaderOverflowMenuContainer";
import { UpsellTrialButtonContainer } from "@ms/planner-container-controls/lib/local/containers/upsellTrialButton/UpsellTrialButtonContainer";
import { ClipboardLinkBundled, CopyAddBundled, ShareBundled } from "@ms/planner-generic-controls/lib/local/icons";
import { PlanHeaderControls } from "@ms/planner-presentational-controls/lib/local/planHeader/planHeaderControls/PlanHeaderControls";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useInteractionTracker } from "@ms/planner-presentational-controls/lib/local/hooks/useInteractionTracker";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { getPrimaryViewContentWidth } from "@ms/planner-container-controls/lib/local/utilities/TaskEditorPanelLayoutUtilities";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Models
import { SharePlanDialogMode } from "@ms/planner-models/lib/local/client/modal/ISharePlanModalData";
import { UserGroupRelation } from "@ms/planner-models/lib/local/service/graph-legacy/modernGroup/Enumerations";
import { GroupVisibilityMode } from "@ms/planner-models/lib/local/service/graph/enumerations/GroupVisibilityMode";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { getPlanRoutingDetails } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import findIndex from "lodash/findIndex";
import * as React from "react";
// Constant for this component
const ComponentName = "PlanHeaderLinksContainer";
/**
 * Smart component for plan header links/actions
 */ export const PlanHeaderLinksContainer = ({ planId })=>{
    const configProvider = useConfigProvider();
    const { engagementLogger } = useLoggers();
    const [appContextStore, planStore, routeStore, viewStore, modernGroupsStore, permissionsStore, modalStore, taskStore] = useStores([
        Store.AppContext,
        Store.Plan,
        Store.Route,
        Store.View,
        Store.ModernGroups,
        Store.Permissions,
        Store.Modal,
        Store.Tasks
    ]);
    const primaryViewContentWidth = getPrimaryViewContentWidth({
        viewStore,
        modalStore,
        taskStore,
        planStore,
        permissionsStore
    }, configProvider);
    const [modalActionCreator, linksActionCreator, sharePlanViewAc, userActionCreator] = useActionCreators([
        ActionCreator.Modal,
        ActionCreator.Links,
        ActionCreator.SharePlanView,
        ActionCreator.User
    ]);
    const { startInteraction } = useInteractionTracker();
    const currentPlan = planStore.getPlan(planId).data ?? null;
    if (!currentPlan) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null);
    }
    /**
     * Open share plan modal
     */ const openSharePlanModal = (initialMode)=>{
        engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.ShareButtonClicked, {
            planId
        });
        startInteraction({
            name: "OpenSharePlanDialog",
            key: planId
        }, {
            name: ViewName.SharePlanDialog,
            partitionKey: appContextStore.getCurrentUserIdentitySet().user.id
        }, "planHeader");
        sharePlanViewAc.fetchViewData(planId);
        modalActionCreator.openModal({
            modalType: ModalType.SharePlan,
            planId,
            userIds: []
        });
    };
    /**
     * Callback to open publish plan template dialog
     */ const openPublishPlanTemplateDialog = async ()=>{
        engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.AddPublishAsTemplateButtonClicked, {
            planId
        });
        modalActionCreator.openModal({
            modalType: ModalType.PublishPlanAsTemplate,
            planId,
            onDismiss: ()=>{}
        });
    };
    /**
     * Callback to copy link to plan
     */ const copyLinkToPlan = async ()=>{
        const subRouteType = routeStore.getCurrentSubRouteType();
        const routingDetails = getPlanRoutingDetails(subRouteType, planId, {
            planName: currentPlan.displayName
        });
        engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.CopyPlanLinkButtonClicked, {
            planId
        });
        linksActionCreator.copyLinkToPlan(routingDetails);
    };
    if (!(configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab)) {
        let onClickJoinPlan;
        const currentUserId = appContextStore.getAppContext().userId;
        const planContainer = currentPlan.container;
        if (currentUserId && planContainer.containerType === ContainerType.Group) {
            const modernGroup = modernGroupsStore.getModernGroup(planContainer.containerId).data;
            const membersCount = modernGroupsStore.getModernGroupMembers(planContainer.containerId).data?.length ?? 0;
            const ownerIds = modernGroupsStore.getModernGroupOwners(planContainer.containerId).data;
            const ownerIndex = findIndex(ownerIds, (ownerId)=>{
                return ownerId === currentUserId;
            });
            // Check if the user is a non-member by looking at existing owners and members of the group
            const currentUserRelationToGroup = ownerIndex !== -1 ? UserGroupRelation.Owner : membersCount > 0 ? UserGroupRelation.Member : UserGroupRelation.NonMember;
            if (currentUserRelationToGroup === UserGroupRelation.NonMember && modernGroup?.visibility === GroupVisibilityMode.Public) {
                onClickJoinPlan = ()=>{
                    engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.JoinPlanButtonClicked, {
                        planId
                    });
                    userActionCreator.addMembersToContainer([
                        currentUserId
                    ], planContainer, true);
                };
            }
        }
        const staticMenuItems = [];
        planContainer.containerType !== ContainerType.ToDo && staticMenuItems.push({
            key: "sharePlan",
            text: Strings.PlanHeader_ShareText,
            icon: /*#__PURE__*/ React.createElement(ShareBundled, null),
            onClick: ()=>openSharePlanModal(SharePlanDialogMode.People),
            dataTestId: "plan-header-share-plan-menu-item"
        });
        staticMenuItems.push({
            key: "copyLinkToPlan",
            text: Strings.PlanHeader_CopyLinkToPlanText,
            icon: /*#__PURE__*/ React.createElement(ClipboardLinkBundled, null),
            onClick: copyLinkToPlan,
            dataTestId: "plan-header-copy-link-menu-item"
        });
        // Check if export is allowed by usage rights/sensitivity labels
        const enableContentLevelSensitivityLabels = configProvider().flights.EnableContentLevelSensitivityLabelsEvo;
        const sensitivityPermissions = permissionsStore.getPlanPermissionsFromUserRightsObject(planId);
        const canExportDisallowedBySensitivity = enableContentLevelSensitivityLabels && !sensitivityPermissions.canExportAllowedBySensitivity;
        const isPublishTemplateUnavailableForToDo = planContainer.containerType === ContainerType.ToDo;
        const publishTemplateDisabledTooltip = isPublishTemplateUnavailableForToDo ? Strings.PlanHeader_PublishTemplateDisabledForToDoListPlanTooltip : canExportDisallowedBySensitivity ? Strings.PlanHeader_PublishTemplateDisabledBySensitivityTooltip : undefined;
        const publishTemplateMenuItem = configProvider().flights.EnableBasicPlanCustomTemplate ? {
            key: "publishAsTemplate",
            text: Strings.PlanHeader_AddPublishAsATemplateText,
            icon: /*#__PURE__*/ React.createElement(CopyAddBundled, null),
            onClick: openPublishPlanTemplateDialog,
            disabled: isPublishTemplateUnavailableForToDo || canExportDisallowedBySensitivity,
            title: publishTemplateDisabledTooltip,
            dataTestId: "plan-header-copy-plan-as-template-menu-item"
        } : undefined;
        return /*#__PURE__*/ React.createElement(PlanHeaderControls, {
            plan: currentPlan,
            showMoreButton: true,
            headerOverflowMenu: /*#__PURE__*/ React.createElement(HeaderOverflowMenuContainer, null),
            onClickJoinPlan: onClickJoinPlan,
            shareMenuButtonProps: {
                publishTemplateMenuItem: publishTemplateMenuItem,
                staticMenuItems: staticMenuItems,
                width: primaryViewContentWidth
            },
            trialButton: /*#__PURE__*/ React.createElement(UpsellTrialButtonContainer, {
                entryPoint: TryPremiumEntryPoint.Header
            }),
            width: primaryViewContentWidth
        });
    } else {
        return /*#__PURE__*/ React.createElement(HeaderOverflowMenuContainer, null);
    }
};
